"use client";

import React, { useEffect, useRef } from "react";

interface ConstellationBackgroundProps {
  isLight: boolean;
}

export function ConstellationBackground({ isLight }: ConstellationBackgroundProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let animationFrameId: number;
    let particles: Particle[] = [];
    const mouse = { x: -100, y: -100 };

    const isMobile = window.innerWidth < 768;

    const getSettings = () => ({
      count: isMobile ? 35 : 180,
      // Shorter connection distance = fewer lines drawn per frame
      distance: isMobile ? 90 : 170,
      distanceSq: isMobile ? 90 * 90 : 170 * 170,
      mouseDistance: isMobile ? 0 : 220,
      mouseDistanceSq: isMobile ? 0 : 220 * 220,
    });

    let settings = getSettings();

    let frameCount = 0;
    const frameSkip = isMobile ? 2 : 1;

    class Particle {
      x: number;
      y: number;
      vx: number;
      vy: number;
      size: number;

      constructor(width: number, height: number) {
        this.x = Math.random() * width;
        this.y = Math.random() * height;
        this.vx = (Math.random() - 0.5) * 0.6;
        this.vy = (Math.random() - 0.5) * 0.6;
        this.size = Math.random() * 3 + 1.5;
      }

      update(width: number, height: number) {
        this.x += this.vx;
        this.y += this.vy;

        if (this.x < 0 || this.x > width) this.vx *= -1;
        if (this.y < 0 || this.y > height) this.vy *= -1;
      }

      draw(ctx: CanvasRenderingContext2D, color: string) {
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fillStyle = color;
        ctx.fill();
      }
    }

    const init = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      settings = getSettings();
      particles = [];
      for (let i = 0; i < settings.count; i++) {
        particles.push(new Particle(canvas.width, canvas.height));
      }
    };

    const animate = () => {
      frameCount++;

      // On mobile skip every other render, but always schedule the next frame
      // This keeps physics updates smooth while halving GPU paint work
      if (frameCount % frameSkip !== 0) {
        // Still update positions so motion stays smooth when we do paint
        for (let i = 0; i < particles.length; i++) {
          particles[i].update(canvas.width, canvas.height);
        }
        animationFrameId = requestAnimationFrame(animate);
        return;
      }

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const color = isLight ? "rgba(249, 115, 22, 0.7)" : "rgba(59, 130, 246, 0.7)";
      const lineColor = isLight ? "rgba(249, 115, 22, 0.2)" : "rgba(59, 130, 246, 0.2)";

      const len = particles.length;
      for (let i = 0; i < len; i++) {
        const p = particles[i];
        p.update(canvas.width, canvas.height);
        p.draw(ctx, color);

        // Connections between particles — use squared distance to avoid sqrt
        for (let j = i + 1; j < len; j++) {
          const p2 = particles[j];
          const dx = p.x - p2.x;
          const dy = p.y - p2.y;
          const distSq = dx * dx + dy * dy;

          if (distSq < settings.distanceSq) {
            const dist = Math.sqrt(distSq); // only sqrt when we actually draw
            ctx.beginPath();
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(p2.x, p2.y);
            ctx.strokeStyle = lineColor;
            ctx.lineWidth = (1 - dist / settings.distance) * 1.5;
            ctx.stroke();
          }
        }

        // Connection to mouse (disabled on mobile via mouseDistance = 0)
        if (settings.mouseDistance > 0) {
          const mdx = p.x - mouse.x;
          const mdy = p.y - mouse.y;
          const mdistSq = mdx * mdx + mdy * mdy;
          if (mdistSq < settings.mouseDistanceSq) {
            const mdist = Math.sqrt(mdistSq);
            ctx.beginPath();
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(mouse.x, mouse.y);
            ctx.strokeStyle = isLight ? "rgba(249, 115, 22, 0.4)" : "rgba(59, 130, 246, 0.4)";
            ctx.lineWidth = (1 - mdist / settings.mouseDistance) * 2;
            ctx.stroke();
          }
        }
      }

      animationFrameId = requestAnimationFrame(animate);
    };

    const handleMouseMove = (e: MouseEvent) => {
      mouse.x = e.clientX;
      mouse.y = e.clientY;
    };

    const handleResize = () => {
      init();
    };

    // Only listen for mouse on desktop
    if (!isMobile) {
      window.addEventListener("mousemove", handleMouseMove);
    }
    window.addEventListener("resize", handleResize);

    init();
    animate();

    return () => {
      if (!isMobile) {
        window.removeEventListener("mousemove", handleMouseMove);
      }
      window.removeEventListener("resize", handleResize);
      cancelAnimationFrame(animationFrameId);
    };
  }, [isLight]);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full pointer-events-none"
      style={{ filter: "blur(0.3px)" }}
    />
  );
}
